import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import classnames from 'classnames'
import { LoadingSection, showToast } from '@quickmd-llc/design-system'
import { setErrorMemberPage } from '../../store/member/MemberActions'
import { useDeleteMember } from './hooks/useDeleteMember'
import { useResendInvite } from './hooks/useResendInvite'
import { useSetMemberActivation } from './hooks/useSetMemberActivation'
import { useOverwriteMemberProductivity } from './hooks/useOverwriteMemberProductivity'
import { useValidateMemberPhone } from './hooks/useValidateMemberPhone'
import { errorCodes, getUserFriendlyErrorMessage } from '../../utils/errormessages'
import { getProductivityList, onClickSafeMemberButton } from '../../utils/commonMembersPage'
import { changeTabs } from '../../store/management/ManagementActions'
import { setError } from '../../store/newMember/newMemberActions'
import {
  CLINIC_MEMBERS_TEXT,
  FIRST_NAME_TEXT,
  INTAKE_FORM_TYPE_EMAIL_TEXT,
  LAST_NAME_TEXT,
  MANAGEMENT_LABEL,
  MANAGEMENT_PAGE_ROUTE,
  NOT_FOUND_PAGE_ROUTE,
  QUICK_MD_TITLE_TEXT,
} from '../../utils/constants'
import { tabNames, text, userManagementTypes } from './Constants'
import { formatNameForPatientChartTitle } from '../patient-chart-page/helper'
import { getAdjustProductivityRequestBody } from './helper'
import ProductivityPopUp from './ProductivityPopUp/ProductivityPopUp'
import { PageHeader } from './components/PageHeader'
import { PendingInvitationBanner } from './components/PendingInvitationBanner'
import { ResendInviteSuccessPopup } from './components/ResendInviteSuccessPopup'
import { ExtraLongWordsTooltip, type ExtraLongWordsTooltipState } from './components/ExtraLongWordsTooltip'
import { useDeleteLicense } from './hooks/useDeleteLicense'
import { useUpdateLicenseVisibility } from './hooks/useUpdateLicenseVisibility'
import { usePutProvidersUserIdProductivityoverrides } from '../../api/generated/doctor/doctor'
import { useQueryClient } from '@tanstack/react-query'
import { useMember } from './hooks/useMember'
import { useEditMember } from './hooks/useEditMember'
import { GeneralTab } from './general-tab/GeneralTab'
import { PermissionsSection } from './permissions-section/PermissionsSection'
import { useCanManagePermissions } from './permissions-section/useCanManagePermissions'
import { LicensesTab } from './licenses-tab/LicensesTab'
import type { SaveProductivityArgs } from './productivity-section/ProductivitySection'
import { getProductivityErrorMessage } from './productivity-section/getProductivityErrorMessage'
import { DeleteLicensePopup } from './license-form/DeleteLicensePopup'
import type { License } from './license-form/transformLicense'
import { DeleteUserPopup } from './DeleteUserPopup/DeleteUserPopup'
import { statesOptions as statesOptionsList } from '../../utils/common'
import { useMemberStoreValues } from './hooks/useMemberStoreValues'
import { useAppDispatch } from '../../redux/hooks/useAppDispatch'
import { useAppSelector } from '../../redux/hooks/useAppSelector'
import { findIana } from 'windows-iana'
import CalendarPageUntyped from '../CalendarPage/CalendarPage'
import { buildLicensesArray, type LicenseBlock } from './helpers/buildLicensesArray'
import { DiscardChangesPopup } from '../../components/discard-changes-popup/DiscardChangesPopup'
import { useDiscardChangesGuard } from '../../components/discard-changes-popup/useDiscardChangesGuard'
import type {
  AdminApiInContract,
  DoctorAdminApiOutContract,
  DoctorLicenseAdminApiOutContract,
  DoctorProductivityOverrideAdminApiContract,
  PhoneApiContract,
  UpdateDoctorAdminApiInContract,
} from '../../api/generated/pwrdbyQuickMDAdminsApi.schemas'
import type { MemberData } from './hooks/useMember'
import { isRecord, readStringProp } from '../../utils/narrowing'
import type {
  ProductivityForEditShape,
  ProductivityForEditUpdater,
  RawProductivityServiceType,
} from '../../components/productivity-list/productivity-list-item/ProductivityListItem'

const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

/**
 * Provider-shaped view of the member. `useMember` returns an admin-or-provider union; the page
 * (like the legacy `.jsx`) reads provider-only fields throughout. `AdminApiOutContract` is a
 * structural subset of `DoctorAdminApiOutContract` (all fields optional), so an admin member
 * simply has the provider-only fields as `undefined` at runtime — matching these optional types.
 */
type MemberView = DoctorAdminApiOutContract & { UserType?: string | null }

const readProductivityOverrides = (
  member: MemberData,
): DoctorProductivityOverrideAdminApiContract[] | null | undefined =>
  'ProductivityOverrides' in member ? member.ProductivityOverrides : undefined

const readLicenses = (member: MemberData): DoctorLicenseAdminApiOutContract[] | null | undefined =>
  'Licenses' in member ? member.Licenses : undefined

const readPhoneNumber = (member: MemberData): PhoneApiContract | null | undefined =>
  'PhoneNumber' in member ? member.PhoneNumber : undefined

const buildMemberView = (member: MemberData | undefined): MemberView | undefined => {
  if (!member) return undefined
  return {
    ...member,
    ProfessionalTitles: ('ProfessionalTitles' in member ? member.ProfessionalTitles : null) ?? null,
    ProductivityOverrides: readProductivityOverrides(member) ?? null,
    NationalProviderIdentifier:
      ('NationalProviderIdentifier' in member ? member.NationalProviderIdentifier : null) ?? null,
    Licenses: readLicenses(member) ?? null,
    PhoneNumber: readPhoneNumber(member) ?? null,
    IsExcludedFromMarketingReports:
      ('IsExcludedFromMarketingReports' in member ? member.IsExcludedFromMarketingReports : false) ?? false,
  }
}

type LicenseDeleteTarget = {
  licenseBlockId: string
  stateLicenseValue: string
}

type ViewModeVisitType = {
  VisitTypeId?: string | null | undefined
  Name?: string | null | undefined
  Duration?: number | undefined
  visitTypeInitialDuration?: number | undefined
  customDuration?: number | undefined
  IsExemptFromNewPatientSpacing?: boolean | undefined
  MaxConsecutiveNewPatientVisits?: number | null | undefined
  IsNewPatientSpacingEnabled?: boolean | undefined
  [key: string]: unknown
}

type ViewModeServiceType = {
  ServiceTypeId?: string | null
  value?: string
  Name?: string
  VisitTypes: ViewModeVisitType[]
  [key: string]: unknown
}

function narrowFormValue(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

// `CalendarPage` is untyped JS (its `memo`-wrapped props infer as `object`). Re-type it to the
// props the calendar tab actually passes; `useAdminWrapper` toggles the admin layout wrapper.
const CalendarPage: React.ComponentType<{ crumbsLabel?: string; useAdminWrapper?: boolean }> = CalendarPageUntyped

// The edit-member request body is assembled by the untyped `onClickSafeMemberButton` JS util.
// All fields on both in-contracts are optional, so a plain object satisfies either shape; the
// `role` discriminator (already validated by the caller) decides which contract applies.
function isAdminApiInContract(body: unknown): body is AdminApiInContract {
  return isRecord(body)
}

function isUpdateDoctorApiInContract(body: unknown): body is UpdateDoctorAdminApiInContract {
  return isRecord(body)
}

export default function Member() {
  const params = new URLSearchParams(window.location.search)
  const id = params.get('id')
  const isOpen = useRef(false)
  const userType = params.get('type')

  const dispatch = useAppDispatch()
  const deleteLicense = useDeleteLicense(id ?? '')
  const updateLicenseVisibility = useUpdateLicenseVisibility(id ?? '')
  const queryClient = useQueryClient()
  const productivityMutation = usePutProvidersUserIdProductivityoverrides()
  // Member read migrated to React Query (admin vs provider GET). `member` carries the synthesized
  // UserType the old GET_MEMBER_SUCCESS reducer added via extraData.
  const { member: rawMember, queryKey: memberQueryKey, isLoading: memberLoading } = useMember(id ?? '', userType ?? '')
  const member = useMemo(() => buildMemberView(rawMember), [rawMember])
  const navigate = useNavigate()
  const location = useLocation()
  const { state: locationState } = location

  const deleteMemberMutation = useDeleteMember(id ?? '', userType ?? '')
  const resendInviteMutation = useResendInvite(id ?? '', userType ?? '')
  const setMemberActivationMutation = useSetMemberActivation(id ?? '', userType ?? '')
  const overwriteProductivityMutation = useOverwriteMemberProductivity(id ?? '')
  const validatePhoneMutation = useValidateMemberPhone(id ?? '')
  const editMemberMutation = useEditMember(id ?? '')

  const { phoneIsValid, setPhoneIsValid } = validatePhoneMutation

  const [deleteSuccess, setDeleteSuccess] = useState(false)
  const [resendInviteSuccess, setResendInviteSuccess] = useState(false)

  const {
    loadingServiceType,
    isLoggedIn,
    loading,
    serviceTypes,
    serviceLoading,
    tabs,
    error,
    pageState,
    errorNewMember,
    duplicateLicensesInfo,
    setMemberProviderTabs,
  } = useMemberStoreValues()
  const profile = useAppSelector((state) => state.accountSettings.adminProfile)
  const calendarLoading = useAppSelector((state) => state.calendarPageReducer.loading)
  const canManagePermissions = useCanManagePermissions()

  const [isEditMode, setIsEditMode] = useState(false)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [licensesArray, setLicensesArray] = useState<LicenseBlock[] | null>([])
  const [extraLongWordsToolTip, setExtraLongWordsToolTip] = useState<ExtraLongWordsTooltipState | null>(null)
  const [profileImgInputValue, setProfileImgInputValue] = useState<File | string | null | undefined>(member?.ImageUrl)
  const [email, setEmail] = useState<string | null | undefined>(member?.Email)
  const [emailLocalError, setEmailLocalError] = useState('')
  const [firstName, setFirstName] = useState<string | null | undefined>(member?.FirstName)
  const [firstNameLocalError, setFirstNameLocalError] = useState('')
  const [prefix, setPrefix] = useState<string>(member?.Prefix ?? ' ')
  const [lastName, setLastName] = useState<string | null | undefined>(member?.LastName)
  const [lastNameLocalError, setLastNameLocalError] = useState('')
  const [searchParams] = useSearchParams()

  const [isExcludedFromMarketingReports, setIsExcludedFromMarketingReports] = React.useState(
    !!member?.IsExcludedFromMarketingReports,
  )

  const [professionalTitle, setProfessionalTitle] = useState('')
  const [professionalTitleLocalError, setProfessionalTitleLocalError] = useState('')
  const [npi, setNpi] = useState('')
  const [suffix, setSuffix] = useState<string>(member?.Suffix ?? ' ')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [phoneCountry, setPhoneCountry] = useState('us')
  const [phoneCountryCode, setPhoneCountryCode] = useState('1')
  const [isPhoneCorrect, setIsPhoneCorrect] = useState<boolean | ''>('')
  const [phoneLocalError, setPhoneLocalError] = useState('')
  const [lastPhoneLocalError, setLastLocalPhoneError] = useState('')
  const [isBackToManagementPageLoading, setIsBackToManagementPageLoading] = useState(false)
  const [oldNumber, setOldNumber] = useState(phoneNumber)
  const [isOldNumberCorrect, setIsOldNumberCorrect] = useState<boolean | ''>(isPhoneCorrect)
  const [isImageChanged, setIsImageChanged] = useState(false)
  // Set by the user-facing General-tab edit handlers below (not the programmatic member-sync
  // effects, which call the raw setters). Drives the unsaved-changes guard while in edit mode.
  const [isMemberEdited, setIsMemberEdited] = useState(false)
  const [phoneCheckLoading, setPhoneCheckLoading] = useState(false)
  const [selectedServiceTypeArray, setSelectedServiceTypeArray] = useState<RawProductivityServiceType[]>([])
  const [selectedServiceTypeViewModeArray, setSelectedServiceTypeViewModeArray] = useState<ViewModeServiceType[]>([])
  const [isProviderWithLicense, setIsProviderWithLicense] = useState(false)
  const [isDeleteLicensePopUpOpened, setIsDeleteLicensePopUpOpened] = useState(false)
  const [licenseForDelete, setLicenseForDelete] = useState<LicenseDeleteTarget | null>(null)
  const [productivityForEdit, setProductivityForEdit] = useState<ProductivityForEditShape | null>(null)

  const { FirstName, LastName } = member ?? {}
  const defaultTimeZone = profile.TimeZone ? findIana(profile.TimeZone)[0] : null
  const pageTitle = formatNameForPatientChartTitle(FirstName ?? undefined, LastName ?? undefined)

  // Replaces the `ui_kit_qmd` `useAdjustPageHeadEntities({ title })` (new-members precedent).
  useEffect(() => {
    document.title = pageTitle
  }, [pageTitle])

  // Refreshes the displayed member by invalidating its React Query cache (admin or provider
  // key). Replaces the legacy `dispatch(getMember(id, userType))` refresh-after-mutation calls.
  const refreshMember = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: memberQueryKey })
  }, [queryClient, memberQueryKey])

  // License delete success is handled by useDeleteLicense hook (React Query); the hook already
  // invalidates the provider member key, so the displayed member refreshes automatically.
  const onDeleteLicense = () => {
    if (!licenseForDelete) return
    deleteLicense.mutate(licenseForDelete.licenseBlockId, {
      onSuccess: () => {
        setIsDeleteLicensePopUpOpened(false)
        setLicenseForDelete(null)
        refreshMember()
      },
      onError: () => {
        showToast({ type: 'error', message: 'Failed to delete license', position: 'bottom-right' })
      },
    })
  }

  const onCloseProductivityPopUp = useCallback(() => {
    setProductivityForEdit(null)
  }, [])

  // Adapter: the child's setter type operates on a non-null ProductivityForEditShape; bridge it to
  // the nullable state setter (a no-op while the popup is closed / productivityForEdit is null).
  const updateProductivityForEdit = useCallback((updater: ProductivityForEditUpdater) => {
    setProductivityForEdit((prev) => (prev ? updater(prev) : prev))
  }, [])

  const onProductivityOverwriteSuccess = useCallback(() => {
    onCloseProductivityPopUp()
    refreshMember()
  }, [onCloseProductivityPopUp, refreshMember])

  const onSaveProductivity = (value: ProductivityForEditShape) => {
    const requestBody = getAdjustProductivityRequestBody(value)
    overwriteProductivityMutation.mutate(requestBody, {
      onSuccess: onProductivityOverwriteSuccess,
    })
  }

  const onOpenDeleteLicensePopUp = () => {
    setIsDeleteLicensePopUpOpened(true)
  }

  const memberNameRow = useMemo(
    () =>
      `${member?.Prefix ? `${member?.Prefix} ` : ''}${member?.FirstName} ${member?.LastName} ${member?.Suffix ?? ''}`,
    [member?.FirstName, member?.LastName, member?.Prefix, member?.Suffix],
  )

  useEffect(() => {
    document.title = QUICK_MD_TITLE_TEXT

    const tab = searchParams.get('tab')
    const state = {
      search: '',
      providerId: searchParams.get('id'),
      providerName: memberNameRow,
      ImageUrl: member?.ImageUrl,
      timezone: defaultTimeZone ?? currentTimeZone,
      label: MANAGEMENT_LABEL,
      calendarRoute: '/management/member',
      route: MANAGEMENT_PAGE_ROUTE,
    }

    if (!locationState) {
      void navigate(location, {
        replace: true,
        state,
      })
      setMemberProviderTabs(tab ?? '', false, state)
    } else {
      setMemberProviderTabs(tab ?? '', false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only navigation/tab bootstrap; deps intentionally omitted to run once
  }, [])

  useEffect(() => {
    if (!isEditMode) {
      if (isImageChanged) {
        setIsImageChanged(false)
      }

      if (licensesArray?.length) {
        setIsProviderWithLicense(true)
      } else {
        setIsProviderWithLicense(false)
      }
    }
  }, [licensesArray, isEditMode, isImageChanged])

  useEffect(() => {
    if (isEditMode && isProviderWithLicense) {
      const selectedVisitTypes = (licensesArray ?? [])
        .map((license) => license.licenseVisitTypesOptions.filter((t) => t.isActive))
        .flat()

      // getProductivityList is untyped JS; it returns raw-array-form objects (RawProductivityServiceType).
      // Sorted in place, exactly as the legacy .jsx did.
      const newArray = getProductivityList(selectedVisitTypes, selectedServiceTypeArray)
      newArray.sort((a: ViewModeServiceType, b: ViewModeServiceType) => (a.value ?? '').localeCompare(b.value ?? ''))
      if (newArray.length) {
        newArray.forEach((productivity: ViewModeServiceType) => {
          productivity.VisitTypes.sort((a, b) => (a.Name ?? '').localeCompare(b.Name ?? ''))
        })
      }
      setSelectedServiceTypeArray(newArray)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedServiceTypeArray intentionally omitted to avoid a re-sort loop
  }, [licensesArray, isEditMode, isProviderWithLicense])

  useEffect(() => {
    if (isProviderWithLicense) {
      const selectedServiceTypes: ViewModeServiceType[] = getProductivityList(
        (licensesArray ?? []).map((license) => license.licenseVisitTypesOptions.filter((t) => t.isActive)).flat(),
      )
      selectedServiceTypes.sort((a, b) => (a.value ?? '').localeCompare(b.value ?? ''))
      if (selectedServiceTypes.length) {
        selectedServiceTypes.forEach((productivity) => {
          productivity.VisitTypes.sort((a, b) => (a.Name ?? '').localeCompare(b.Name ?? ''))
        })
      }
      const overrides = member?.ProductivityOverrides ?? []
      if (overrides.length > 0) {
        overrides.forEach((el) => {
          const findIndexProductivity = selectedServiceTypes.findIndex((option) =>
            option.VisitTypes.some((visitType) => visitType.VisitTypeId === el.VisitTypeId),
          )
          const target = selectedServiceTypes[findIndexProductivity]
          if (!target) return
          selectedServiceTypes[findIndexProductivity] = {
            ...target,
            VisitTypes: target.VisitTypes.map((t) => {
              if (el.VisitTypeId === t.VisitTypeId) {
                return {
                  ...t,
                  Duration: el.Duration ?? undefined,
                  visitTypeInitialDuration: t.Duration,
                  IsExemptFromNewPatientSpacing: el.IsExemptFromNewPatientSpacing,
                  MaxConsecutiveNewPatientVisits: el.MaxConsecutiveNewPatientVisits,
                }
              }
              return t
            }),
          }
        })
        setSelectedServiceTypeViewModeArray(selectedServiceTypes)
      } else {
        setSelectedServiceTypeViewModeArray(selectedServiceTypes)
      }
    }
  }, [member, serviceTypes, licensesArray, isProviderWithLicense])

  // Transform selectedServiceTypeViewModeArray into flat productivity visit types for new UI
  const productivityVisitTypes = useMemo(() => {
    if (!selectedServiceTypeViewModeArray?.length) return []
    return selectedServiceTypeViewModeArray.flatMap((serviceType) =>
      (serviceType.VisitTypes || []).map((vt) => ({
        VisitTypeId: vt.VisitTypeId ?? '',
        Name: vt.Name ?? '',
        ServiceName: serviceType.value ?? serviceType.Name ?? '',
        Duration: vt.Duration ?? null,
        DefaultDuration: vt.visitTypeInitialDuration ?? vt.Duration ?? null,
        IsExemptFromNewPatientSpacing: vt.IsExemptFromNewPatientSpacing ?? false,
        MaxConsecutiveNewPatientVisits: vt.MaxConsecutiveNewPatientVisits ?? null,
        IsNewPatientSpacingEnabled: vt.IsNewPatientSpacingEnabled ?? false,
      })),
    )
  }, [selectedServiceTypeViewModeArray])

  // Both productivity mutations resolve to whether the write landed, so the card can stay in edit
  // mode on failure instead of closing over an unsaved change. Without this a 4xx looked exactly
  // like a mis-click: the editor shut and the previous value reappeared with no explanation.
  const handleSaveProductivity = useCallback(
    async ({
      visitTypeId,
      duration,
      isExemptFromNewPatientSpacing,
      maxConsecutiveNewPatientVisits,
    }: SaveProductivityArgs): Promise<boolean> => {
      try {
        await productivityMutation.mutateAsync({
          userId: id ?? '',
          data: {
            Create: [
              {
                VisitTypeId: visitTypeId,
                Duration: duration,
                IsExemptFromNewPatientSpacing: isExemptFromNewPatientSpacing,
                MaxConsecutiveNewPatientVisits: maxConsecutiveNewPatientVisits,
              },
            ],
            Delete: [],
          },
        })
        refreshMember()
        return true
      } catch (err) {
        showToast({ type: 'error', message: getProductivityErrorMessage(err), position: 'bottom-right' })
        return false
      }
    },
    [id, productivityMutation, refreshMember],
  )

  const handleResetProductivity = useCallback(
    async (visitTypeId: string): Promise<boolean> => {
      try {
        await productivityMutation.mutateAsync({
          userId: id ?? '',
          data: { Create: [], Delete: [visitTypeId] },
        })
        refreshMember()
        return true
      } catch (err) {
        showToast({ type: 'error', message: getProductivityErrorMessage(err), position: 'bottom-right' })
        return false
      }
    },
    [id, productivityMutation, refreshMember],
  )

  const onClickSafeChangesButton = useCallback(
    (isPhoneValidated: boolean | '', phoneError: string) => {
      if (phoneNumber && !isPhoneValidated && !phoneError) {
        setPhoneCheckLoading(true)
        validatePhoneMutation.validate({
          Number: `+${phoneCountryCode}${phoneNumber}`,
          Prefix: `+${phoneCountryCode}`,
          CountryCode: phoneCountry.toUpperCase(),
        })
        return
      }
      setPhoneCheckLoading(false)
      onClickSafeMemberButton(
        dispatch,
        setErrorMemberPage,
        firstName,
        setFirstNameLocalError,
        () => {},
        lastName,
        setLastNameLocalError,
        () => {},
        email,
        setEmailLocalError,
        () => {},
        prefix,
        suffix,
        member?.UserType,
        () => {},
        () => {},
        isExcludedFromMarketingReports,
        professionalTitle,
        () => {},
        setProfessionalTitleLocalError,
        licensesArray ?? [],
        setLicensesArray,
        member,
        'edit',
        phoneNumber ?
          {
            Number: `+${phoneCountryCode}${phoneNumber}`,
            Prefix: `+${phoneCountryCode}`,
            CountryCode: phoneCountry.toUpperCase(),
          }
        : null,
        phoneError,
        undefined,
        undefined,
        npi,
        (role: string, body: unknown) => {
          const image = isImageChanged && profileImgInputValue instanceof Blob ? profileImgInputValue : null
          const onEditDone = {
            onSuccess: () => {
              setIsImageChanged(false)
              setIsEditMode(false)
            },
          }
          if (role === userManagementTypes.ADMIN && isAdminApiInContract(body)) {
            void editMemberMutation.editMember({ role: 'Admin', body, image }, onEditDone)
          } else if (isUpdateDoctorApiInContract(body)) {
            void editMemberMutation.editMember({ role: 'Provider', body, image }, onEditDone)
          }
        },
      )
    },
    [
      dispatch,
      email,
      firstName,
      lastName,
      licensesArray,
      member,
      phoneCountry,
      phoneCountryCode,
      phoneNumber,
      prefix,
      professionalTitle,
      suffix,
      isExcludedFromMarketingReports,
      npi,
      validatePhoneMutation,
      editMemberMutation,
      isImageChanged,
      profileImgInputValue,
    ],
  )

  useEffect(() => {
    if (phoneIsValid !== null && isEditMode) {
      setIsPhoneCorrect(phoneIsValid)
      setIsOldNumberCorrect(phoneIsValid)
      const errorMsg = getUserFriendlyErrorMessage(errorCodes.ENTER_VALID, null, text.PHONE_NUMBER_LOWER)
      if (!phoneIsValid) {
        setPhoneLocalError(errorMsg)
        setLastLocalPhoneError(errorMsg)
      }
      if (phoneCheckLoading) {
        onClickSafeChangesButton(phoneIsValid, phoneIsValid ? '' : errorMsg)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs only when phoneIsValid changes; other deps intentionally omitted to avoid re-validating
  }, [phoneIsValid])

  useEffect(() => {
    dispatch(changeTabs(CLINIC_MEMBERS_TEXT))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only; sets the active management tab once
  }, [])

  useEffect(() => {
    if (member?.Email) {
      setEmail(member.Email)
    }
    if (member?.FirstName) {
      setFirstName(member.FirstName)
    }
    if (member?.LastName) {
      setLastName(member.LastName)
    }
    if (member?.Prefix) {
      setPrefix(member.Prefix)
    }
    if (member?.Suffix) {
      setSuffix(member.Suffix)
    }
  }, [serviceTypes, member, loadingServiceType])

  useEffect(() => {
    if (!isEditMode && member) {
      setEmail(member.Email)
      setFirstName(member.FirstName)
      setLastName(member.LastName)
      setPrefix(member.Prefix ?? ' ')
      setSuffix(member.Suffix ?? ' ')
      setIsExcludedFromMarketingReports(!!member?.IsExcludedFromMarketingReports)
      setNpi(member?.NationalProviderIdentifier ?? '')
      setProfessionalTitle(Array.isArray(member?.ProfessionalTitles) ? member.ProfessionalTitles.join(';') : '')
    }
  }, [isEditMode, member])

  useEffect(() => {
    if (isEditMode && userType === 'Provider') {
      isOpen.current = true
      setPhoneLocalError('')
      setLastLocalPhoneError('')
      if (member?.PhoneNumber?.Number && member?.PhoneNumber?.Prefix && member?.PhoneNumber?.CountryCode) {
        setPhoneNumber(member.PhoneNumber.Number.slice(member.PhoneNumber.Prefix.length))
        setPhoneCountry(member.PhoneNumber.CountryCode.toLowerCase())
        setPhoneCountryCode(member.PhoneNumber.Prefix.slice(1))
        setIsPhoneCorrect(true)
        setPhoneIsValid(true)
        setOldNumber(member.PhoneNumber.Number.slice(member.PhoneNumber.Prefix.length))
        setIsOldNumberCorrect(true)
      } else {
        setPhoneIsValid(null)
      }
    }

    isOpen.current = false
    // eslint-disable-next-line react-hooks/exhaustive-deps -- syncs phone fields only on edit-mode toggle; member deps intentionally omitted
  }, [isEditMode])

  useEffect(() => {
    if (isRecord(duplicateLicensesInfo)) {
      const dupLicenseNumber = readStringProp(duplicateLicensesInfo, 'licenseNumber')
      const dupLicensedBy = readStringProp(duplicateLicensesInfo, 'licensedBy')
      const dupStateCode = readStringProp(duplicateLicensesInfo, 'stateCode')
      const correctLicenseArray = (licensesArray ?? []).map((t) =>
        (
          t.licenseNumberValue === dupLicenseNumber &&
          t.licensedByValue?.trim() === dupLicensedBy.trim() &&
          (t.stateLicenseValue ?? '').includes(dupStateCode)
        ) ?
          {
            ...t,
            licenseNumberError: true,
            licenseNumberLocalError: getUserFriendlyErrorMessage(errorCodes.DUPLICATE_LICENSES_FROM_SERVER, null, null),
          }
        : t,
      )
      setLicensesArray(correctLicenseArray)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reacts only to new duplicate-license info; licensesArray intentionally omitted to avoid a loop
  }, [duplicateLicensesInfo])

  useEffect(() => {
    setProfileImgInputValue(member?.ImageUrl)
  }, [member?.ImageUrl])

  useEffect(() => {
    const container = document.getElementById('container')
    if (container?.parentElement) {
      container.parentElement.style.overflowX = 'hidden'
    }
    const intervalId = setInterval(() => {
      window.dispatchEvent(new Event('resize'))
    }, 100)
    return () => clearInterval(intervalId)
  }, [])

  const onPressCancel = () => {
    setIsEditMode(false)
    setProfileImgInputValue(member?.ImageUrl)
    if (!member?.PhoneNumber?.CountryCode) {
      setPhoneCountry('us')
      setPhoneCountryCode('1')
    }
    if (!member?.PhoneNumber?.Number) {
      setPhoneNumber('')
    }
  }

  useEffect(() => {
    if (!isEditMode) {
      setLastNameLocalError('')
      setFirstNameLocalError('')
      setEmailLocalError('')
    }
  }, [isEditMode])

  const onSetFormValues = useCallback(
    (type: string, value: string) => {
      switch (type) {
        case 'email': {
          if (value.length < 226) {
            setEmailLocalError('')
            dispatch(setErrorMemberPage())
            dispatch(setError())
            setEmail(value)
          }
          break
        }
        case 'firstName': {
          if (value.length < 51) {
            setFirstNameLocalError('')
            setFirstName(value)
          }
          break
        }
        case 'lastName': {
          if (value.length < 51) {
            setLastNameLocalError('')
            setLastName(value)
          }
          break
        }
        case 'ProfessionalTitle': {
          setProfessionalTitleLocalError('')
          setProfessionalTitle(value)
          break
        }
        case 'NPI': {
          setNpi(value.replace(/\D/g, '').slice(0, 10))
          break
        }
        case 'phoneNumber': {
          if (!Number.isNaN(Number(value)) && !value.endsWith('.')) {
            setPhoneLocalError('')
            if (error) dispatch(setError(''))
            if (value === oldNumber && !isOldNumberCorrect && isOldNumberCorrect !== '') {
              setPhoneLocalError(lastPhoneLocalError)
              setIsPhoneCorrect(false)
            } else if (value === oldNumber && isOldNumberCorrect) {
              setIsPhoneCorrect(true)
            } else {
              setIsPhoneCorrect(false)
            }
            setPhoneNumber(value)
          }
          break
        }
        default:
          break
      }
    },
    [dispatch, error, isOldNumberCorrect, lastPhoneLocalError, oldNumber],
  )

  useEffect(() => {
    if (isBackToManagementPageLoading) {
      dispatch(changeTabs(CLINIC_MEMBERS_TEXT))
      void navigate(MANAGEMENT_PAGE_ROUTE)
      setIsBackToManagementPageLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs only on the back-to-management trigger; navigate/dispatch intentionally omitted
  }, [isBackToManagementPageLoading])

  useEffect(() => {
    if (errorNewMember === getUserFriendlyErrorMessage(errorCodes.ALREADY_ACTIVATED_USER, null)) {
      setEmailLocalError(getUserFriendlyErrorMessage(errorCodes.ALREADY_ACTIVATED_USER, null))
    }
    if (
      errorNewMember === getUserFriendlyErrorMessage(errorCodes.NAME_ALREADY_EXISTS, null, INTAKE_FORM_TYPE_EMAIL_TEXT)
    ) {
      setEmailLocalError(getUserFriendlyErrorMessage(errorCodes.NAME_ALREADY_EXISTS, null, INTAKE_FORM_TYPE_EMAIL_TEXT))
    }
  }, [errorNewMember])

  const emailFieldError =
    (
      error === getUserFriendlyErrorMessage(errorCodes.ENTER_VALID, null, INTAKE_FORM_TYPE_EMAIL_TEXT) ||
      error === getUserFriendlyErrorMessage(errorCodes.NAME_ALREADY_EXISTS, null, INTAKE_FORM_TYPE_EMAIL_TEXT)
    ) ?
      error
    : emailLocalError
  const firstNameFieldError =
    error === getUserFriendlyErrorMessage(errorCodes.ENTER_VALID, null, FIRST_NAME_TEXT) ?
      getUserFriendlyErrorMessage(errorCodes.ENTER_VALID, null, FIRST_NAME_TEXT)
    : firstNameLocalError
  const lastNameFieldError =
    error === getUserFriendlyErrorMessage(errorCodes.ENTER_VALID, null, LAST_NAME_TEXT) ?
      getUserFriendlyErrorMessage(errorCodes.ENTER_VALID, null, LAST_NAME_TEXT)
    : lastNameLocalError

  const onClickEditBtn = (newStatus: boolean) => {
    setIsEditMode(newStatus)
  }

  useEffect(() => {
    // Member read migrated to React Query (useMember) — it fetches on mount automatically.
    return () => {
      isOpen.current = false
    }
  }, [])

  // License-rebuild effect: rebuilds `licensesArray` from `member.Licenses` + `serviceTypes` +
  // `member.ProductivityOverrides`. The ~100-line transform is extracted to the pure
  // `buildLicensesArray` helper (same output); this thin effect calls it and preserves the
  // original `else if (!isEditMode)` clear branch exactly.
  useEffect(() => {
    if (isLoggedIn) {
      const built = buildLicensesArray(member, serviceTypes)
      if (built) {
        setLicensesArray(built)
      } else if (!isEditMode) {
        setLicensesArray([])
        setSelectedServiceTypeViewModeArray([])
        setIsProviderWithLicense(false)
      }
    }
  }, [member, serviceTypes, isLoggedIn, isEditMode])

  const isCalendarTabActive = tabs[2]?.isActive && member?.UserType === userManagementTypes.PROVIDER

  useEffect(() => {
    if (resendInviteSuccess) {
      setTimeout(() => {
        setResendInviteSuccess(false)
      }, 2000)
    }
  }, [resendInviteSuccess])

  const onResendInvite = useCallback(() => {
    resendInviteMutation.mutate({
      onSuccess: () => setResendInviteSuccess(true),
    })
  }, [resendInviteMutation])

  useEffect(() => {
    if (errorNewMember === getUserFriendlyErrorMessage(errorCodes.PROVIDER_NUMBER_NOT_UNIQUE)) {
      setLastLocalPhoneError(getUserFriendlyErrorMessage(errorCodes.PROVIDER_NUMBER_NOT_UNIQUE))
      setPhoneLocalError(getUserFriendlyErrorMessage(errorCodes.PROVIDER_NUMBER_NOT_UNIQUE))
      setIsOldNumberCorrect(false)
    }
    setTimeout(() => {
      if (error) {
        dispatch(setErrorMemberPage())
      }
      if (errorNewMember) {
        dispatch(setError())
      }
    }, 3500)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs only on error changes; dispatch intentionally omitted
  }, [error, errorNewMember])

  // Error-alert → toast: replaces the two inline `ErrorAlert` blocks. Same trigger conditions and
  // same text the original rendered verbatim (`error || errorNewMember` for the general alert;
  // FILL_ALL_FIELDS for the provider-number-not-unique alert). Routed through DS `showToast`
  // (not `useErrorToast`) so the member general alert keeps its verbatim text — `useErrorToast`'s
  // code-collapsing map would drift member's ENTER_VALID/ITEM_DOES_NOT_EXIST text to DEFAULT.
  useEffect(() => {
    if (
      error ||
      errorNewMember === getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS) ||
      errorNewMember === getUserFriendlyErrorMessage(errorCodes.DEFAULT)
    ) {
      showToast({ type: 'error', message: error || errorNewMember, position: 'bottom-right' })
    }
  }, [error, errorNewMember])

  useEffect(() => {
    if (errorNewMember === getUserFriendlyErrorMessage(errorCodes.PROVIDER_NUMBER_NOT_UNIQUE)) {
      showToast({
        type: 'error',
        message: getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS),
        position: 'bottom-right',
      })
    }
  }, [errorNewMember])

  const onBlur = () => {
    if (phoneNumber === oldNumber) {
      if (isOldNumberCorrect) {
        setIsPhoneCorrect(true)
      } else {
        setIsPhoneCorrect(false)
      }
    }
    setTimeout(() => {
      if (!isOpen.current) return
      if (phoneNumber && phoneNumber !== oldNumber) {
        setOldNumber(phoneNumber)
        validatePhoneMutation.validate({
          Number: `+${phoneCountryCode}${phoneNumber}`,
          Prefix: `+${phoneCountryCode}`,
          CountryCode: phoneCountry.toUpperCase(),
        })
      }
    }, 150)
  }

  const onCloseDeleteMemberConfirmationPopUp = useCallback(() => {
    setShowDeleteConfirmation(false)
  }, [])
  const onDeleteMember = useCallback(() => {
    setShowDeleteConfirmation(false)
    deleteMemberMutation.mutate({
      onSuccess: () => setDeleteSuccess(true),
    })
  }, [deleteMemberMutation])

  const onClickSaveChangesBtn = useCallback(() => {
    onClickSafeChangesButton(isPhoneCorrect, phoneLocalError)
  }, [isPhoneCorrect, onClickSafeChangesButton, phoneLocalError])

  const onClickPrimaryEditBtn = useCallback(() => onClickEditBtn(true), [])

  const onAccountActiveChange = useCallback(() => {
    if (!member) return
    setMemberActivationMutation.mutate(!member.IsActive, {
      onSuccess: refreshMember,
    })
  }, [member, setMemberActivationMutation, refreshMember])

  const onChangeActiveTab = (newActiveTabName: string) => {
    setMemberProviderTabs(newActiveTabName)
  }

  // Clear the edited flag whenever we leave edit mode (cancel or save success).
  useEffect(() => {
    if (!isEditMode) setIsMemberEdited(false)
  }, [isEditMode])

  // Guard the two exit paths (Cancel + nav-bar) once the user has edited while in edit mode.
  const {
    isPopupOpen: isDiscardPopupOpen,
    handleCancel: onCancelEdit,
    handleDiscard: onDiscardChanges,
    handleKeepEditing: onKeepEditing,
  } = useDiscardChangesGuard({ isDirty: isEditMode && isMemberEdited, onConfirmLeave: onPressCancel })

  const isGeneralTabActiveForProviderOnly =
    (tabs[0]?.isActive ?? false) || member?.UserType === userManagementTypes.ADMIN
  const isLicenseTabActiveForProviderOnly =
    tabs[1]?.isActive &&
    (userType === userManagementTypes.PROVIDER || member?.UserType === userManagementTypes.PROVIDER)

  if (!isLoggedIn) {
    void navigate('/')
  }

  // Legacy parity: `errorCodes.USER_DOES_NOT_EXISTS` did not exist on errorCodes, so this resolved
  // to `getUserFriendlyErrorMessage(undefined)` → the DEFAULT message. Preserved verbatim.
  if (error === getUserFriendlyErrorMessage(undefined)) {
    return (
      <Navigate
        to={{
          pathname: NOT_FOUND_PAGE_ROUTE,
        }}
      />
    )
  }

  if (deleteSuccess) {
    void navigate(`${MANAGEMENT_PAGE_ROUTE}${locationState?.search ?? ''}`)
  }

  const isProviderMode = userType === userManagementTypes.PROVIDER
  const isLoadingOverlay = [
    loading,
    memberLoading,
    serviceLoading,
    calendarLoading,
    isBackToManagementPageLoading,
    loadingServiceType,
    pageState === 'loading',
    editMemberMutation.isPending,
    deleteMemberMutation.isPending,
    resendInviteMutation.isPending,
    setMemberActivationMutation.isPending,
    overwriteProductivityMutation.isPending,
    validatePhoneMutation.isPending,
    deleteSuccess,
  ].some(Boolean)

  return (
    <div className='relative flex flex-1 flex-col bg-surface-disabled'>
      <DiscardChangesPopup
        isOpen={isDiscardPopupOpen}
        entityName={memberNameRow}
        onDiscard={onDiscardChanges}
        onKeepEditing={onKeepEditing}
      />
      {/* Full-page loading overlay. LoadingSection is `flex-1` and centers its spinner in a
          flex-col parent; rendered in-flow it split height with the content div and was pinned to
          the top. Pulled out of flow (absolute inset-0) so it covers the whole page and stays
          centered, above page content but below modals/toasts. */}
      {isLoadingOverlay ?
        <div className='absolute inset-0 z-fixed flex bg-surface-disabled'>
          <LoadingSection />
        </div>
      : null}
      <ExtraLongWordsTooltip tooltip={extraLongWordsToolTip} />
      <ProductivityPopUp
        onCloseProductivityPopUp={onCloseProductivityPopUp}
        productivityForEdit={productivityForEdit}
        setProductivityForEdit={updateProductivityForEdit}
        selectedServiceTypeViewModeArray={selectedServiceTypeViewModeArray || []}
        setExtraLongWordsToolTip={setExtraLongWordsToolTip}
        setSelectedServiceTypeArray={setSelectedServiceTypeArray}
        onSaveProductivity={onSaveProductivity}
      />
      <ResendInviteSuccessPopup isOpen={resendInviteSuccess} onClose={() => setResendInviteSuccess(false)} />
      {isDeleteLicensePopUpOpened && licenseForDelete ?
        <DeleteLicensePopup
          stateName={licenseForDelete.stateLicenseValue || licenseForDelete.licenseBlockId}
          onConfirm={onDeleteLicense}
          onCancel={() => {
            setIsDeleteLicensePopUpOpened(false)
            setLicenseForDelete(null)
          }}
        />
      : null}
      <DeleteUserPopup
        isOpen={showDeleteConfirmation}
        onClose={onCloseDeleteMemberConfirmationPopUp}
        onConfirm={onDeleteMember}
      />
      <div
        data-testid='pw_member-container'
        className='flex h-auto min-h-[300px] w-full min-w-[670px] flex-1 flex-col self-start p-sp-6'>
        {/* Page header — unified breadcrumb-style heading for admin and provider.
            Tabs only for providers (admins only have a General tab). */}
        {member ?
          <PageHeader
            providerName={memberNameRow}
            activeTab={tabs.find((t) => t.isActive)?.name ?? tabNames.GENERAL}
            onTabChange={onChangeActiveTab}
            showTabs={userType === userManagementTypes.PROVIDER}
          />
        : null}
        <div
          className={classnames('flex w-full', {
            // member-body-admin → justify-start; otherwise space-between
            'justify-start': member?.UserType,
            'justify-between': !member?.UserType,
            // calendar tab: original row layout, margin-top reset to 0 (.calendar-view)
            'mt-0 flex-row': tabs[2]?.isActive,
            // non-calendar: fill height (flex-1) for provider, and for admin in edit mode so the
            // GeneralTab sticky Save/Cancel footer pins to the bottom. Admin view mode uses a
            // natural-height column so the Permissions section sits right below General instead of
            // being pushed down by GeneralTab's own flex-1.
            'flex-1 flex-col': !tabs[2]?.isActive && (isProviderMode || isEditMode),
            'flex-col': !tabs[2]?.isActive && !isProviderMode && !isEditMode,
            'mt-sp-3 gap-sp-3': isProviderMode && !tabs[2]?.isActive,
            'mt-[55px]': !isProviderMode && !tabs[2]?.isActive,
          })}>
          {/* CalendarPage reads providerId from its router location state, not props — the legacy
              `providerId` prop was always ignored, so only the real `useAdminWrapper` prop is passed. */}
          {isCalendarTabActive && locationState ?
            <CalendarPage useAdminWrapper={false} />
          : null}

          {/* Pending invitation banner */}
          {member && !member.IsVerified && !loading && isProviderMode && !isCalendarTabActive ?
            <PendingInvitationBanner onDelete={() => setShowDeleteConfirmation(true)} onResend={onResendInvite} />
          : null}

          {/* General Tab */}
          {isGeneralTabActiveForProviderOnly && member && !tabs[2]?.isActive ?
            <GeneralTab
              member={member}
              isEditMode={isEditMode}
              onEdit={onClickPrimaryEditBtn}
              onCancel={onCancelEdit}
              onSave={onClickSaveChangesBtn}
              onSetFormValues={(field, value) => {
                setIsMemberEdited(true)
                onSetFormValues(field, narrowFormValue(value))
              }}
              profileImgInputValue={profileImgInputValue}
              isImageChanged={isImageChanged}
              setProfileImgInputValue={setProfileImgInputValue}
              setIsImageChanged={(value) => {
                setIsMemberEdited(true)
                setIsImageChanged(value)
              }}
              firstName={firstName ?? ''}
              lastName={lastName ?? ''}
              prefix={prefix?.trim() ? prefix : ' '}
              suffix={suffix?.trim() ? suffix : ' '}
              setPrefix={(value) => {
                setIsMemberEdited(true)
                setPrefix(value)
              }}
              setSuffix={(value) => {
                setIsMemberEdited(true)
                setSuffix(value)
              }}
              email={email ?? ''}
              phoneNumber={phoneNumber}
              professionalTitle={professionalTitle}
              npi={npi}
              onPhoneBlur={onBlur}
              isExcludedFromMarketingReports={isExcludedFromMarketingReports}
              onExcludeFromReportingChange={(value) => {
                setIsMemberEdited(true)
                setIsExcludedFromMarketingReports(value)
              }}
              onAccountActiveChange={onAccountActiveChange}
              emailError={emailFieldError}
              phoneError={phoneLocalError}
              professionalTitleError={professionalTitleLocalError}
              firstNameError={firstNameFieldError}
              lastNameError={lastNameFieldError}
            />
          : null}

          {/* Admin permissions — General tab, view mode; only for admins who can manage permissions */}
          {(
            isGeneralTabActiveForProviderOnly &&
            !tabs[2]?.isActive &&
            !isEditMode &&
            member?.UserType === userManagementTypes.ADMIN &&
            canManagePermissions
          ) ?
            <div className='mt-sp-3 w-full'>
              <PermissionsSection userId={id ?? ''} />
            </div>
          : null}

          {/* Licenses & Productivity Tab */}
          {isLicenseTabActiveForProviderOnly && !isEditMode ?
            <LicensesTab
              licenses={member?.Licenses ?? []}
              serviceTypes={
                serviceTypes?.flatMap?.((s) =>
                  (s.VisitTypes ?? []).map((v) => ({ VisitTypeId: v.VisitTypeId ?? '', Name: v.Name ?? '' })),
                ) || []
              }
              userId={id ?? ''}
              onDeleteLicense={(license: License) => {
                const match = statesOptionsList.find((s: string) => s.endsWith(` ${license.StateCode}`))
                setLicenseForDelete({
                  licenseBlockId: license.LicenseId,
                  stateLicenseValue: match ?? license.StateCode,
                })
                onOpenDeleteLicensePopUp()
              }}
              onVisibilityChange={(license: License, visibility: string) => {
                updateLicenseVisibility.mutateAsync(license, visibility).catch(() => {
                  showToast({ type: 'error', message: 'Failed to update visibility', position: 'bottom-right' })
                })
              }}
              productivityVisitTypes={productivityVisitTypes}
              onSaveProductivity={handleSaveProductivity}
              onResetProductivity={handleResetProductivity}
              isProductivitySaving={productivityMutation.isPending}
            />
          : null}
        </div>
      </div>
    </div>
  )
}
